/**
 * GameController — pure game logic extracted from useGameState.ts
 *
 * Provides pure state transformation functions for the Sbarazzo card game.
 * Both CPU mode (useGameState) and online mode (matchmaking.ts) will call
 * GameController for game logic — they differ only in WHO provides the move.
 *
 * NO side effects: no Firebase, no setTimeout, no UI state.
 * All functions return NEW state objects (immutable).
 */

import { Card, GameState } from '../types';
import {
  createDeck,
  shuffle,
  getPossibleCaptures,
  calculateRoundScores,
  isScopaMove,
  RoundScoreResults,
} from './gameUtils';
import { GAME_CONSTANTS } from './constants';

// Re-export from gameUtils
export { calculateRoundScores, isScopaMove, getPossibleCaptures };
export type { RoundScoreResults };

export interface GameController {
  gameState: GameState;
  playCard(card: Card, capture?: Card[]): GameState;
  performCapture(playerIndex: number, playedCard: Card, capturedCards: Card[]): GameState;
  handleNextRound(): GameState;
  getPossibleCapturesForCard(card: Card): Card[][];
  isScopaMove(playedCard: Card, table: Card[], capturedCards: Card[]): boolean;
  isMyTurn(playerIndex: number): boolean;
}

/**
 * Factory function to create a GameController instance.
 * The controller holds a mutable reference to state internally but all
 * exported methods return new immutable state objects.
 */
export function createGameController(initialState: GameState): GameController {
  let _state = initialState;

  return {
    get gameState(): GameState {
      return _state;
    },

    playCard(card: Card, _capture?: Card[]): GameState {
      // Remove card from current player's hand
      const newPlayers = _state.players.map((p, i) =>
        i === _state.currentPlayerIndex
          ? { ...p, hand: p.hand.filter(c => c.id !== card.id) }
          : p
      );

      // Advance to next player
      const nextPlayerIndex = (_state.currentPlayerIndex + _state.numPlayers - 1) % _state.numPlayers;

      _state = {
        ..._state,
        table: [..._state.table, card],
        players: newPlayers,
        currentPlayerIndex: nextPlayerIndex,
        lastPlayedCard: null,
      };

      return _state;
    },

    /**
     * Perform a capture with given cards.
     * Returns the FINAL new state (no animation staging — caller handles visuals).
     */
    performCapture(playerIndex: number, playedCard: Card, capturedCards: Card[]): GameState {
      const activePlayer = _state.players[playerIndex];

      // Check for scopa (cleared table)
      const scopa = isScopaMove(playedCard, _state.table, capturedCards);

      // Remove captured cards from table
      const newTable = _state.table.filter(
        c => !capturedCards.some(cc => cc.id === c.id)
      );

      // Build new captured pile for the player
      const newCaptured = [...activePlayer.captured, playedCard, ...capturedCards];

      // Remove played card from player's hand
      const newHand = activePlayer.hand.filter(c => c.id !== playedCard.id);

      // Update players array
      const newPlayers = _state.players.map((p, i) => {
        if (i !== playerIndex) return p;
        return {
          ...p,
          hand: newHand,
          captured: newCaptured,
          scope: scopa ? p.scope + 1 : p.scope,
        };
      });

      // Build messages and scopa event
      let newMessages = _state.messages;
      let newScopaEvent = _state.scopaEvent ?? undefined;

      if (scopa) {
        newMessages = [
          `${activePlayer.name}: SCOPA!`,
          ..._state.messages.slice(0, 4),
        ];
        newScopaEvent = { playerIndex, timestamp: Date.now() };
      }

      _state = {
        ..._state,
        table: newTable,
        players: newPlayers,
        lastPlayedCard: null,
        scopaEvent: newScopaEvent,
        messages: newMessages,
        currentPlayerIndex: (playerIndex + _state.numPlayers - 1) % _state.numPlayers,
        lastToCapture: playerIndex,
      };

      return _state;
    },

    /**
     * Advance to the next round: deal new table and hands,
     * advance roundStartPlayer, reset round-specific state.
     */
    handleNextRound(): GameState {
      const deck = shuffle(createDeck());
      const table = deck.splice(0, GAME_CONSTANTS.TABLE_CARDS);
      const nextRoundStart = (_state.roundStartPlayer + _state.numPlayers - 1) % _state.numPlayers;

      const newPlayers = _state.players.map(p => {
        const hand = deck.splice(0, GAME_CONSTANTS.CARDS_PER_PLAYER);
        return {
          ...p,
          hand,
          captured: [],
          scope: 0,
          currentRoundScore: 0,
          hasDeclaredLowHand: false,
          hasDeclaredThreeAces: false,
          lowHandCount: 0,
          threeAcesCount: 0,
        };
      });

      _state = {
        ..._state,
        deck,
        table,
        players: newPlayers,
        currentPlayerIndex: nextRoundStart,
        roundStartPlayer: nextRoundStart,
        phase: 'playing',
        lastToCapture: null,
        lastPlayedCard: null,
        scopaEvent: undefined,
        lowHandEvent: undefined,
        roundResults: undefined,
        messages: ['Nuovo round iniziato!'],
      };

      return _state;
    },

    /**
     * Get all possible capture combinations for a card.
     */
    getPossibleCapturesForCard(card: Card): Card[][] {
      return getPossibleCaptures(card, _state.table);
    },

    /**
     * Determine if a move is a scopa (captured all cards on the table).
     */
    isScopaMove(playedCard: Card, table: Card[], capturedCards: Card[]): boolean {
      // Uses the imported isScopaMove from gameUtils (shadowed by method name — explicit import alias needed)
      return isScopaMove(playedCard, table, capturedCards);
    },

    /**
     * Check if it is a given player's turn.
     */
    isMyTurn(playerIndex: number): boolean {
      return _state.currentPlayerIndex === playerIndex;
    },
  };
}